import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, Ticket } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import { getContent, getContentList, saveContent, type ContentEntity } from '../../lib/api/content'
import { Field, TextInput, inputCls, Switch } from '../config/ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const isoToDate = (v: unknown): string => {
  const m = String(v ?? '').match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}
const dateToIso = (d: string): string => (d ? `${d}T00:00:00Z` : '')

type Form = {
  name: string
  code: string
  discount: string
  reusable: boolean
  enabled: boolean
  transportIds: string[]
  fromDay: string
  toDay: string
}

function seed(el: El): Form {
  const v = (el.validity ?? {}) as El
  const refs = Array.isArray(el.transports) ? (el.transports as El[]) : []
  return {
    name: String(el.name ?? ''),
    code: String(el.code ?? ''),
    discount: String(el.discount ?? ''),
    reusable: el.reusable == null ? true : isYes(el.reusable),
    enabled: el.enabled == null ? true : isYes(el.enabled),
    transportIds: refs.map((r) => String((r as { id?: unknown }).id ?? '')).filter(Boolean),
    fromDay: isoToDate(v.fromDay),
    toDay: isoToDate(v.toDay),
  }
}

export function CouponEditDrawer({
  couponId,
  onClose,
  listKey,
}: {
  couponId: string | null // null = create new
  onClose: () => void
  listKey: readonly unknown[]
}) {
  const qc = useQueryClient()
  const isNew = couponId === null
  const [form, setForm] = useState<Form | null>(null)
  const [entity, setEntity] = useState<ContentEntity | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['coupon', couponId],
    queryFn: () => getContent('coupon', couponId!),
    enabled: Boolean(couponId),
  })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (isNew) {
      setForm(seed({}))
      setEntity(null)
      setError(null)
    } else if (detail.data) {
      setEntity(detail.data)
      setForm(seed(detail.data.elements as El))
      setError(null)
    }
  }, [isNew, detail.data])

  const transportOptions = useMemo(
    () =>
      (transports.data?.data ?? []).map((t) => ({
        id: String(t.id),
        label: String((t.elements.name as string) ?? (t.elements.label as string) ?? t.id),
      })),
    [transports.data],
  )

  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const toggleTransport = (id: string) =>
    setForm((f) =>
      f
        ? { ...f, transportIds: f.transportIds.includes(id) ? f.transportIds.filter((x) => x !== id) : [...f.transportIds, id] }
        : f,
    )

  async function save() {
    if (!form) return
    if (!form.code.trim()) {
      setError('Il codice è obbligatorio.')
      return
    }
    if (!form.name.trim()) {
      setError('Il nome è obbligatorio.')
      return
    }
    if (!form.discount.trim()) {
      setError('Inserisci uno sconto (es. 10 o 10%).')
      return
    }
    setError(null)
    setSaving(true)
    try {
      // round-trip on edit (preserve untouched elements incl. useCount/validity extras); fresh on create
      const baseEls = (entity?.elements as El) ?? {}
      const next: ContentEntity = isNew
        ? { id: '', modelId: 'coupon', elements: {} }
        : { ...(entity as ContentEntity), elements: { ...baseEls } }
      const e = next.elements as El
      e.name = form.name.trim()
      e.code = form.code.trim()
      e.discount = form.discount.trim()
      e.reusable = form.reusable
      e.enabled = form.enabled
      e.transports = form.transportIds.map((id) => ({ id, modelId: 'transport' }))
      e.validity = {
        ...((baseEls.validity as El) ?? {}),
        fromDay: dateToIso(form.fromDay),
        toDay: dateToIso(form.toDay),
      }
      await saveContent('coupon', next)
      await qc.invalidateQueries({ queryKey: listKey })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const loading = !isNew && detail.isLoading

  return (
    <Drawer
      open
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">{isNew ? 'Nuovo coupon' : 'Modifica coupon'}</span>}
      subtitle={form?.code ? form.code : isNew ? 'Crea un codice sconto' : 'Caricamento…'}
      footer={
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Il salvataggio scrive sul gestionale live.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving || !form}
              className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? 'Crea coupon' : 'Salva'}
            </button>
          </div>
        </div>
      }
    >
      {loading || !form ? (
        <div className="flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : detail.isError ? (
        <div className="py-20 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il coupon.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Codice" hint="quello digitato dal cliente">
              <div className="flex h-10 items-center gap-1.5 rounded-lg border border-line bg-card px-3 focus-within:border-verdigris">
                <Ticket className="h-4 w-4 text-faint" />
                <input
                  value={form.code}
                  disabled={saving}
                  onChange={(e) => patch({ code: e.target.value.toUpperCase() })}
                  className="w-full bg-transparent font-mono text-[13.5px] font-bold uppercase tracking-wide text-coal outline-none disabled:opacity-50"
                  placeholder="VENEZIA10"
                />
              </div>
            </Field>
            <Field label="Sconto" hint="10 = €  ·  10% = percentuale">
              <TextInput value={form.discount} disabled={saving} onChange={(v) => patch({ discount: v })} placeholder="10%" />
            </Field>
          </div>

          <Field label="Nome / campagna">
            <TextInput value={form.name} disabled={saving} onChange={(v) => patch({ name: v })} placeholder="Campagna estate — newsletter" />
          </Field>

          <Field label="Tipi di veicolo" hint="lascia vuoto per tutti i veicoli">
            <div className="flex flex-wrap gap-1.5">
              {transportOptions.length === 0 && <span className="text-[12px] text-faint">Caricamento…</span>}
              {transportOptions.map((o) => {
                const on = form.transportIds.includes(o.id)
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={saving}
                    onClick={() => toggleTransport(o.id)}
                    className={cn(
                      'rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition-colors',
                      on ? 'border-verdigris bg-verdigris text-white' : 'border-line bg-card text-muted hover:text-coal',
                    )}
                  >
                    {o.label}
                  </button>
                )
              })}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valido dal" hint="opzionale">
              <input
                type="date"
                value={form.fromDay}
                disabled={saving}
                onChange={(e) => patch({ fromDay: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Valido fino al" hint="opzionale">
              <input
                type="date"
                value={form.toDay}
                disabled={saving}
                onChange={(e) => patch({ toDay: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-6 border-t border-line-soft pt-4">
            <label className="flex items-center gap-2.5 text-[13.5px] font-semibold text-coal">
              <Switch on={form.reusable} disabled={saving} onClick={() => patch({ reusable: !form.reusable })} label="Riutilizzabile" />
              Riutilizzabile
            </label>
            <label className="flex items-center gap-2.5 text-[13.5px] font-semibold text-coal">
              <Switch on={form.enabled} disabled={saving} onClick={() => patch({ enabled: !form.enabled })} label="Attivo" />
              Attivo
            </label>
          </div>

          {!isNew && (
            <p className="text-[12px] text-faint">
              Utilizzi registrati: <b className="text-muted">{String((entity?.elements as El)?.useCount ?? (entity?.elements as El)?.reuseNumber ?? 0)}</b>
            </p>
          )}
        </div>
      )}
    </Drawer>
  )
}
